import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import dayjs from "dayjs"

import { useLazyGetAlertsQuery, useLazyGetAlertsUnseenCountQuery } from "store/api/alertsApi"
import { useAppSelector } from "store/hooks"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { transformText } from "utils/transformText"
import { AlertStatusBadge } from "components/common/AlertStatusBadge"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"
import { Spinner } from "components/common/Spinner"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  AlertDate,
  AlertItem,
  AlertStatusWrapper,
  AlertTitle,
  LoadingContainer,
  NoAlertsText,
  StyledWidgetButton,
  UnseenCountBadge,
  Widget,
  WidgetButton,
  WidgetContent,
  WidgetTitle,
} from "../styled"

export const RecentAlertsWidget = () => {
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [triggerGetAlerts, { data, isFetching }] = useLazyGetAlertsQuery()
  const [triggerGetAlertsUnseenCount] = useLazyGetAlertsUnseenCountQuery()
  const [unseenCount, setUnseenCount] = useState<number>(0)
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasAlertsAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Alerts) : false
  const selectedDevice = devices[currentDevice]

  useEffect(() => {
    if (!selectedDevice?.sn || !hasAlertsAccess) {
      setUnseenCount(0)
      return
    }

    const fetchUnseenCount = async () => {
      try {
        const result = await triggerGetAlertsUnseenCount({ device_sn: selectedDevice.sn })
        if (result.data?.data !== undefined) {
          setUnseenCount(result.data.data)
        } else {
          setUnseenCount(0)
        }
      } catch (error) {
        console.error("Failed to fetch unseen count:", error)
        setUnseenCount(0)
      }
    }

    const fetchAlerts = async () => {
      await triggerGetAlerts({
        sn: selectedDevice.sn,
        per_page: 2,
        page: 1,
      })
    }

    fetchUnseenCount().then(() => {
      fetchAlerts()
    })
  }, [selectedDevice?.sn, triggerGetAlerts, triggerGetAlertsUnseenCount, hasAlertsAccess])

  const recentAlerts = useMemo(() => {
    const alertsList = data?.data || []
    return alertsList.slice(0, 2)
  }, [data?.data])

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("YYYY-MM-DD")
  }

  const handleViewAll = () => {
    navigate(`/customers/${id}/alerts`)
  }

  if (!selectedDevice) {
    return null
  }

  return (
    <Widget>
      <WidgetTitle>
        Recent Alerts
        {unseenCount > 0 && <UnseenCountBadge>{unseenCount > 99 ? "99+" : unseenCount}</UnseenCountBadge>}
      </WidgetTitle>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.Alerts}>
        <WidgetContent>
          {isFetching ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : recentAlerts.length === 0 ? (
            <NoAlertsText>No alerts for this device</NoAlertsText>
          ) : (
            <>
              {recentAlerts.map(alert => (
                <AlertItem key={alert.id}>
                  <div>
                    <AlertTitle>{transformText(alert.type)}</AlertTitle>
                    <AlertDate>{formatDate(alert.date)}</AlertDate>
                  </div>
                  <AlertStatusWrapper>
                    <AlertStatusBadge status={alert.status} />
                  </AlertStatusWrapper>
                </AlertItem>
              ))}
            </>
          )}
        </WidgetContent>
        <WidgetButton>
          <StyledWidgetButton onClick={handleViewAll} size="auto" color="blueBordered">
            View All
          </StyledWidgetButton>
        </WidgetButton>
      </RestrictedAccessWidget>
    </Widget>
  )
}
